import { useState, useRef, useEffect } from 'react';
import { initI18n, useTranslation } from '../../shared/i18n/index';
import { PublicLayout } from '../../shared/components';
import { detectInAppBrowser, openInExternalBrowser } from '../../shared/utils/inAppBrowserDetection';

const LoginApp = ({
  railsEnv,
  enabledProviders = {},
  onLoginSuccess,
  onLoginError,
  locale = 'vi',
  authMode = 'session', // 'session' (default) or 'jwt' for mobile app
  kidDomainMode = false // When true, only show kid login (no tabs)
}) => {
  // Initialize i18n only once on mount
  const i18nInitialized = useRef(false);
  if (!i18nInitialized.current) {
    initI18n(locale);
    i18nInitialized.current = true;
  }
  const { t } = useTranslation();

  // Bypass users for dev/test mode
  const [bypassUsers, setBypassUsers] = useState([]);

  // Check URL parameter for initial tab
  // In kidDomainMode, always show kid tab
  const getInitialTab = () => {
    if (kidDomainMode) return 'kid';
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('as') === 'kid' ? 'kid' : 'parent';
  };

  const [activeTab, setActiveTab] = useState(getInitialTab);
  const [parentEmail, setParentEmail] = useState('');
  const [kidUsername, setKidUsername] = useState('');
  const [kidPassword, setKidPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Check for in-app browser directly using our utility
  const inAppBrowserName = detectInAppBrowser();

  const isTestMode = railsEnv === 'test' || railsEnv === 'development';

  // Fetch bypass users in dev/test mode
  useEffect(() => {
    if (isTestMode) {
      const fetchPromise = fetch('/api/auth/bypass_users');
      // Handle case where fetch might return undefined (e.g., in unit tests)
      if (fetchPromise && typeof fetchPromise.then === 'function') {
        fetchPromise
          .then(res => res.ok ? res.json() : { users: [] })
          .then(data => setBypassUsers(data.users || []))
          .catch(() => setBypassUsers([]));
      }
    }
  }, [isTestMode]);

  const handleKidLogin = async (e) => {
    e.preventDefault();
    setError('');

    // Debug logging for flaky test investigation
    console.log('[KID_LOGIN_DEBUG] handleKidLogin called');
    console.log('[KID_LOGIN_DEBUG] kidUsername from state:', kidUsername);
    console.log('[KID_LOGIN_DEBUG] kidPassword present from state:', !!kidPassword, 'length:', kidPassword?.length);

    // Also check DOM values directly to compare with React state
    const usernameInput = document.querySelector('[data-testid="kid-username-input"]');
    const passwordInput = document.querySelector('[data-testid="kid-password-input"]');
    console.log('[KID_LOGIN_DEBUG] kidUsername from DOM:', usernameInput?.value);
    console.log('[KID_LOGIN_DEBUG] kidPassword from DOM length:', passwordInput?.value?.length);

    if (!kidUsername.trim() || !kidPassword.trim()) {
      console.log('[KID_LOGIN_DEBUG] Validation failed - missing username or password');
      setError('Username and password are required');
      return;
    }

    setLoading(true);
    console.log('[KID_LOGIN_DEBUG] Starting login request...');

    try {
      const requestBody = {
        type: 'kid',
        username: kidUsername,
        password: kidPassword
      };

      // Include auth_mode for JWT authentication (used by mobile app)
      if (authMode === 'jwt') {
        requestBody.auth_mode = 'jwt';
      }

      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content || '';
      console.log('[KID_LOGIN_DEBUG] CSRF token present:', !!csrfToken);

      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify(requestBody)
      });

      console.log('[KID_LOGIN_DEBUG] Response status:', response.status);
      const data = await response.json();
      console.log('[KID_LOGIN_DEBUG] Response data:', JSON.stringify(data));

      if (response.ok) {
        console.log('[KID_LOGIN_DEBUG] Login successful, calling onLoginSuccess');
        console.log('[KID_LOGIN_DEBUG] redirect_url in response:', data.redirect_url);
        onLoginSuccess?.({ type: 'kid', data });
      } else {
        console.log('[KID_LOGIN_DEBUG] Login failed with error:', data.error);
        setError(data.error || 'Invalid username or password');
        onLoginError?.({ type: 'kid', error: data.error });
      }
    } catch (err) {
      console.error('[KID_LOGIN_DEBUG] Network error:', err.message);
      setError('Network error. Please try again.');
      onLoginError?.({ type: 'kid', error: err.message });
    } finally {
      setLoading(false);
    }
  };

  const handleBypassLogin = async (email) => {
    setLoading(true);
    setError('');
    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content || '';
      const response = await fetch('/api/auth/bypass_login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify({ email })
      });

      const data = await response.json();

      if (response.ok) {
        onLoginSuccess?.({ type: 'bypass', data });
      } else {
        setError(data.error || 'Bypass login failed');
        onLoginError?.({ type: 'bypass', error: data.error });
      }
    } catch (err) {
      setError('Network error. Please try again.');
      onLoginError?.({ type: 'bypass', error: err.message });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenExternalBrowser = () => {
    openInExternalBrowser(window.location.href);
  };

  // Show in-app browser warning for the entire login page
  if (inAppBrowserName) {
    return (
      <PublicLayout t={t} showAuthButtons={false}>
        <div className="container d-flex justify-content-center align-items-center py-5" style={{ minHeight: 'calc(100vh - 56px)' }}>
          <div style={{ width: '448px', maxWidth: '90vw' }}>
            <div className="card border-0 shadow-lg rounded-4">
              <div className="card-body p-5 text-center">
                <div className="alert alert-warning mb-4" role="alert" data-testid="in-app-browser-blocked-message">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" className="bi bi-exclamation-triangle me-2" viewBox="0 0 16 16">
                    <path d="M7.938 2.016A.13.13 0 0 1 8.002 2a.13.13 0 0 1 .063.016.146.146 0 0 1 .054.057l6.857 11.667c.036.06.035.124.002.183a.163.163 0 0 1-.054.06.116.116 0 0 1-.066.017H1.146a.115.115 0 0 1-.066-.017.163.163 0 0 1-.054-.06.176.176 0 0 1 .002-.183L7.884 2.073a.147.147 0 0 1 .054-.057zm1.044-.45a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566z"/>
                    <path d="M7.002 12a1 1 0 1 1 2 0 1 1 0 0 1-2 0zM7.1 5.995a.905.905 0 1 1 1.8 0l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995z"/>
                  </svg>
                  {t('children.sessions.in_app_browser_blocked')}
                </div>
                <button
                  type="button"
                  className="btn btn-primary w-100 py-2 fw-semibold"
                  data-testid="open-external-browser-btn"
                  onClick={handleOpenExternalBrowser}
                  style={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    border: 'none',
                    fontSize: '16px'
                  }}>
                  {t('children.sessions.open_external_browser')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout t={t} showAuthButtons={false}>
      <div className="container d-flex justify-content-center align-items-center py-5" style={{ minHeight: 'calc(100vh - 56px)' }} data-testid="login-screen">
        <div style={{ width: '448px', maxWidth: '90vw' }}>
          <div className="card border-0 shadow-lg rounded-4">
            <div className="card-body p-5">
            {/* Login Header */}
            <div className="text-center mb-4">
              <h1 className="fw-bold fs-2 mb-0 text-dark"
                  {...(isTestMode && { 'data-testid': 'login-title' })}>
                Đăng Nhập
              </h1>
            </div>

            {/* Tabs Navigation - hidden in kid domain mode */}
            {!kidDomainMode && (
              <ul className="nav nav-tabs border-bottom mb-4" role="tablist" data-testid="login-tabs">
                <li className="nav-item flex-fill" role="presentation">
                  <button
                    className={`nav-link w-100 fw-semibold text-center ${activeTab === 'parent' ? 'active' : ''}`}
                    onClick={() => {
                      setActiveTab('parent');
                      setError('');
                      // Update URL without page reload
                      const url = new URL(window.location.href);
                      url.searchParams.delete('as');
                      window.history.replaceState({}, '', url.toString());
                    }}
                    type="button"
                    {...(isTestMode && { 'data-testid': 'parent-tab' })}
                    style={{ fontSize: '16px' }}>
                    Phụ huynh
                  </button>
                </li>
                <li className="nav-item flex-fill" role="presentation">
                  <button
                    className={`nav-link w-100 fw-semibold text-center ${activeTab === 'kid' ? 'active' : ''}`}
                    onClick={() => {
                      setActiveTab('kid');
                      setError('');
                      // Update URL without page reload
                      const url = new URL(window.location.href);
                      url.searchParams.set('as', 'kid');
                      window.history.replaceState({}, '', url.toString());
                    }}
                    type="button"
                    {...(isTestMode && { 'data-testid': 'kid-tab' })}
                    style={{ fontSize: '16px' }}>
                    Trẻ
                  </button>
                </li>
              </ul>
            )}

            {/* Error Message */}
            {error && (
              <div className="alert alert-danger" role="alert"
                   {...(isTestMode && { 'data-testid': 'error-message' })}>
                {error}
              </div>
            )}

            {/* Tab Content */}
            <div className="tab-content">
              {/* Parent Sign In Tab */}
              <div className={`tab-pane fade${activeTab === 'parent' ? ' show active' : ''}`} id="parent-signin-pane" role="tabpanel" aria-labelledby="parent-signin-tab">
                <div className="d-grid gap-3">
                  {enabledProviders.google && (
                    <form method="post" action="/users/auth/google_oauth2" data-turbo="false">
                      <input type="hidden" name="authenticity_token" value={document.querySelector('meta[name="csrf-token"]')?.content || ''} />
                      <button
                        type="submit"
                        className="btn btn-lg btn-outline-primary d-flex align-items-center justify-content-center w-100"
                        {...(isTestMode && { 'data-testid': 'google-login-btn' })}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" className="bi bi-google me-2" viewBox="0 0 16 16">
                          <path d="M15.545 6.558a9.42 9.42 0 0 1 .139 1.626c0 2.434-.87 4.492-2.384 5.885h.002C11.978 15.292 10.158 16 8 16A8 8 0 1 1 8 0a7.689 7.689 0 0 1 5.352 2.082l-2.284 2.284A4.347 4.347 0 0 0 8 3.166c-2.087 0-3.86 1.408-4.492 3.304a4.792 4.792 0 0 0 0 3.063h.003c.635 1.893 2.405 3.301 4.492 3.301 1.078 0 2.004-.276 2.722-.764h-.003a3.702 3.702 0 0 0 1.599-2.431H8v-3.08h7.545z"/>
                        </svg>
                        Đăng nhập bằng Google
                      </button>
                    </form>
                  )}

                  {enabledProviders.facebook && (
                    <form method="post" action="/users/auth/facebook" data-turbo="false">
                      <input type="hidden" name="authenticity_token" value={document.querySelector('meta[name="csrf-token"]')?.content || ''} />
                      <button
                        type="submit"
                        className="btn btn-lg btn-outline-primary d-flex align-items-center justify-content-center w-100"
                        {...(isTestMode && { 'data-testid': 'facebook-login-btn' })}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" className="bi bi-facebook me-2" viewBox="0 0 16 16">
                          <path d="M16 8.049c0-4.446-3.582-8.05-8-8.05C3.58 0-.002 3.603-.002 8.05c0 4.017 2.926 7.347 6.75 7.951v-5.625h-2.03V8.05H6.75V6.275c0-2.017 1.195-3.131 3.022-3.131.876 0 1.791.157 1.791.157v1.98h-1.009c-.993 0-1.303.621-1.303 1.258v1.51h2.218l-.354 2.326H9.25V16c3.824-.604 6.75-3.934 6.75-7.951z"/>
                        </svg>
                        Đăng nhập bằng Facebook
                      </button>
                    </form>
                  )}

                  {enabledProviders.apple && (
                    <form method="post" action="/users/auth/apple" data-turbo="false">
                      <input type="hidden" name="authenticity_token" value={document.querySelector('meta[name="csrf-token"]')?.content || ''} />
                      <button
                        type="submit"
                        className="btn btn-lg btn-outline-dark d-flex align-items-center justify-content-center w-100"
                        {...(isTestMode && { 'data-testid': 'apple-login-btn' })}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" className="bi bi-apple me-2" viewBox="0 0 16 16">
                          <path d="M11.182.008C11.148-.03 9.923.023 8.857 1.18c-1.066 1.156-.902 2.482-.878 2.516.024.034 1.52.087 2.475-1.258.955-1.345.762-2.391.728-2.43Zm3.314 11.733c-.048-.096-2.325-1.234-2.113-3.422.212-2.189 1.675-2.789 1.698-2.854.023-.065-.597-.79-1.254-1.157a3.692 3.692 0 0 0-1.563-.434c-.108-.003-.483-.095-1.254.116-.508.139-1.653.589-1.968.607-.316.018-1.256-.522-2.267-.665-.647-.125-1.333.131-1.824.328-.49.196-1.422.754-2.074 2.237-.652 1.482-.311 3.83-.067 4.56.244.729.625 1.924 1.273 2.796.576.984 1.34 1.667 1.659 1.899.319.232 1.219.386 1.843.067.502-.308 1.408-.485 1.766-.472.357.013 1.061.154 1.782.539.571.197 1.111.115 1.652-.105.541-.221 1.324-1.059 2.238-2.758.347-.79.505-1.217.473-1.282Z"/>
                        </svg>
                        Đăng nhập bằng Apple
                      </button>
                    </form>
                  )}

                  {enabledProviders.zalo && (
                    <form method="post" action="/users/auth/zalo" data-turbo="false">
                      <input type="hidden" name="authenticity_token" value={document.querySelector('meta[name="csrf-token"]')?.content || ''} />
                      <button
                        type="submit"
                        className="btn btn-lg btn-outline-primary d-flex align-items-center justify-content-center w-100"
                        {...(isTestMode && { 'data-testid': 'zalo-login-btn' })}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" className="me-2" viewBox="0 0 48 48">
                          <path fill="#2196F3" d="M24,4C13,4,4,13,4,24s9,20,20,20s20-9,20-20S35,4,24,4z"/>
                          <path fill="#FFF" d="M27.5,34h-7c-0.8,0-1.5-0.7-1.5-1.5v-17c0-0.8,0.7-1.5,1.5-1.5h7c0.8,0,1.5,0.7,1.5,1.5v17C29,33.3,28.3,34,27.5,34z M24,30c1.1,0,2-0.9,2-2s-0.9-2-2-2s-2,0.9-2,2S22.9,30,24,30z M26,20h-4v-4h4V20z"/>
                        </svg>
                        Đăng nhập bằng Zalo
                      </button>
                    </form>
                  )}

                  {isTestMode && bypassUsers.length > 0 && (
                    <div data-testid="bypass-login-btn">
                      <div className="text-center text-muted mb-2" style={{ fontSize: '14px' }}>
                        Bypass Login (Dev/Test Only)
                      </div>
                      {bypassUsers.map((user) => (
                        <button
                          key={user.id}
                          onClick={() => handleBypassLogin(user.email)}
                          disabled={loading}
                          className="btn btn-lg btn-outline-secondary d-flex align-items-center justify-content-center w-100 mb-2"
                          data-testid={`bypass-user-${user.email}`}>
                          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" className="bi bi-key me-2" viewBox="0 0 16 16">
                            <path d="M0 8a4 4 0 0 1 7.465-2H14a.5.5 0 0 1 .354.146l1.5 1.5a.5.5 0 0 1 0 .708l-1.5 1.5a.5.5 0 0 1-.708 0L13 9.207l-.646.647a.5.5 0 0 1-.708 0L11 9.207l-.646.647a.5.5 0 0 1-.708 0L9 9.207l-.646.647A.5.5 0 0 1 8 10h-.535A4 4 0 0 1 0 8zm4-3a3 3 0 1 0 2.712 4.285A.5.5 0 0 1 7.163 9h.63l.853-.854a.5.5 0 0 1 .708 0l.646.647.646-.647a.5.5 0 0 1 .708 0l.646.647.646-.647a.5.5 0 0 1 .708 0l.646.647.793-.793-1-1h-6.63a.5.5 0 0 1-.451-.285A3 3 0 0 0 4 5z"/>
                            <path d="M4 8a1 1 0 1 1-2 0 1 1 0 0 1 2 0z"/>
                          </svg>
                          {user.name} ({user.email})
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Kid Login Tab */}
              <div className={`tab-pane fade${activeTab === 'kid' ? ' show active' : ''}`} id="kid-login-pane" role="tabpanel" aria-labelledby="kid-login-tab">
                <form onSubmit={handleKidLogin} {...(isTestMode && { 'data-testid': 'kid-login-form' })}>
                  {/* Username Input */}
                  <div className="mb-3">
                    <div className="position-relative">
                      <span className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
                          <path d="M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm2-3a2 2 0 1 1-4 0 2 2 0 0 1 4 0zm4 8c0 1-1 1-1 1H3s-1 0-1-1 1-4 6-4 6 3 6 4zm-1-.004c-.001-.246-.154-.986-.832-1.664C11.516 10.68 10.289 10 8 10c-2.29 0-3.516.68-4.168 1.332-.678.678-.83 1.418-.832 1.664h10z"/>
                        </svg>
                      </span>
                      <input
                        type="text"
                        name="username"
                        value={kidUsername}
                        onChange={(e) => {
                          console.log('[KID_LOGIN_DEBUG] Username onChange triggered, new value:', e.target.value);
                          setKidUsername(e.target.value);
                        }}
                        className="form-control ps-5 py-2 border-2"
                        placeholder="Tên đăng nhập"
                        autoComplete="username"
                        disabled={loading}
                        {...(isTestMode && { 'data-testid': 'kid-username-input' })}
                        style={{ fontSize: '16px' }}
                      />
                    </div>
                  </div>

                  {/* Password Input */}
                  <div className="mb-4">
                    <div className="position-relative">
                      <span className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
                          <path d="M8 1a2 2 0 0 1 2 2v4H6V3a2 2 0 0 1 2-2zm3 6V3a3 3 0 0 0-6 0v4a2 2 0 0 0-2 2v5a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2z"/>
                        </svg>
                      </span>
                      <input
                        type="password"
                        name="password"
                        value={kidPassword}
                        onChange={(e) => {
                          console.log('[KID_LOGIN_DEBUG] Password onChange triggered, length:', e.target.value.length);
                          setKidPassword(e.target.value);
                        }}
                        className="form-control ps-5 py-2 border-2"
                        placeholder="Mật khẩu"
                        autoComplete="current-password"
                        disabled={loading}
                        {...(isTestMode && { 'data-testid': 'kid-password-input' })}
                        style={{ fontSize: '16px' }}
                      />
                    </div>
                  </div>

                  {/* Login Button */}
                  <button
                    type="submit"
                    disabled={loading || !kidUsername.trim() || !kidPassword.trim()}
                    aria-disabled={loading || !kidUsername.trim() || !kidPassword.trim() ? 'true' : 'false'}
                    className="btn btn-primary w-100 py-2 fw-semibold"
                    {...(isTestMode && { 'data-testid': 'kid-login-submit' })}
                    style={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      border: 'none',
                      fontSize: '16px'
                    }}>
                    {loading ? 'Đang đăng nhập...' : 'Đăng Nhập'}
                  </button>

                  {/* Forgot Password Link */}
                  <div className="text-center mt-3">
                    <a
                      href="#"
                      className="text-decoration-none text-muted"
                      onClick={(e) => {
                        e.preventDefault();
                        alert('Vui lòng liên hệ người giám hộ để cài mật khẩu mới cho trẻ');
                      }}
                      style={{ fontSize: '14px' }}>
                      Quên mật khẩu?
                    </a>
                  </div>
                </form>
              </div>
            </div>
            </div>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
};

export default LoginApp;
